// src/components/financials/ExpenseManager.js
// Updated: Tuesday, August 4, 2026 at 7:29 PM EDT

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Autocomplete, Tabs, Tab, Badge, Divider, Tooltip, Alert
} from '@mui/material';
import {
  AddCircle, CheckCircle, Delete, AccessTime, History,
  Edit, AccountBalanceWallet, Block, Restore, Lock
} from '@mui/icons-material';
import { db } from '../../services/firebase';
import {
  collection, getDocs, addDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy, getDoc, updateDoc
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import ExpenseBatchImport from './ExpenseBatchImport';
import { approveFinancialExpense, revertFinancialExpense } from '../../services/financialPostingService';

const DEFAULT_FORM_STATE = {
  payee: '',
  amount: '',
  category: '',
  fundId: '',
  date: new Date().toISOString().split('T')[0],
  description: '',
  status: 'pending_approval',
  paymentMethod: '',
  checkNumber: ''
};

const DEFAULT_PAYMENT_METHODS = [
  "Check", "ACH / EFT", "Debit Card", "Credit Card", "Bank Transfer", "Cash", "Other"
];

const ExpenseManager = () => {
  const { userProfile, currentUser } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [funds, setFunds] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Create/Edit State
  const [open, setOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);

  // Review & Reject State
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [currentFundBalance, setCurrentFundBalance] = useState(0);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const churchId = userProfile?.churchId;
  const isFinanceAdmin = userProfile?.financeRole === 'admin';
  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (churchId) {
      fetchExpenses();
      fetchFunds();
      fetchVendors();
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  const fetchExpenses = async () => {
    const q = query(
      collection(db, 'churches', churchId, 'financials', 'data', 'expenses'),
      orderBy('date', 'desc')
    );
    const snap = await getDocs(q);
    setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchFunds = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
    setFunds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchVendors = async () => {
    try {
      const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'vendors'));
      const vendorList = snap.docs.map(d => ({
        name: d.data().name || '',
        defaultCategory: d.data().defaultCategory || ''
      }))
        .filter(v => v.name && typeof v.name === 'string')
        .sort((a, b) => a.name.localeCompare(b.name));

      setVendors(vendorList);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCategories = async () => {
    try {
      const q = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'),
        orderBy('name')
      );
      const snap = await getDocs(q);
      setCategories(snap.docs.map(d => d.data().name));
    } catch (e) {
      console.error(e);
    }
  };

  const canReview = (expense) => {
    if (isSuperAdmin) return true;
    if (!isFinanceAdmin) return false;
    if (expense.createdBy === currentUser.uid) return false;
    return true;
  };

  const handleOpenReview = async (expense) => {
    setSelectedExpense(expense);
    setRejectMode(false);
    setRejectReason('');

    const fundRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', expense.fundId);
    const fundSnap = await getDoc(fundRef);
    if (fundSnap.exists()) {
      setCurrentFundBalance(fundSnap.data().balance || 0);
    }
    setReviewOpen(true);
  };

  const confirmApproval = async () => {
    if (!selectedExpense) return;
    try {
      await approveFinancialExpense(churchId, selectedExpense.id);
      setReviewOpen(false);
      setSelectedExpense(null);
      await Promise.all([fetchExpenses(), fetchFunds()]);
    } catch (e) {
      alert("Error approving: " + e.message);
    }
  };

  const confirmRejection = async () => {
    if (!selectedExpense) return;
    if (!rejectReason.trim()) {
      alert("A rejection reason is required for the audit trail.");
      return;
    }

    try {
      await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'expenses', selectedExpense.id), {
        status: 'rejected',
        rejectedBy: currentUser.uid,
        rejectedAt: serverTimestamp(),
        rejectionNote: rejectReason
      });

      setExpenses(expenses.map(e => e.id === selectedExpense.id ? { ...e, status: 'rejected', rejectionNote: rejectReason } : e));
      setReviewOpen(false);
      setSelectedExpense(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleRevert = async (expense) => {
    if (expense.reconciled) {
      alert("This expense has been reconciled/audited in the Ledger. It cannot be reverted.");
      return;
    }
    const reason = window.prompt("Reason for reverting this approved expense:");
    if (!reason?.trim()) return;
    try {
      await revertFinancialExpense(churchId, expense.id, reason.trim());
      await Promise.all([fetchExpenses(), fetchFunds()]);
    } catch (e) {
      alert("Error reverting: " + e.message);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingExpense(null);
    setFormData(DEFAULT_FORM_STATE);
  };

  const handleNewExpense = () => {
    setEditingExpense(null);
    setFormData(DEFAULT_FORM_STATE);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!formData.payee || !formData.amount || !formData.fundId) return;
    setLoading(true);
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount) };

      if (editingExpense) {
        await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'expenses', editingExpense.id), {
          ...payload, updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'), {
          ...payload, status: 'pending_approval', createdAt: serverTimestamp(), createdBy: currentUser.uid
        });
      }

      handleCloseDialog();
      fetchExpenses();
      setTabValue(0);

    } catch (e) {
      alert(e.message);
    }
    setLoading(false);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      payee: expense.payee,
      amount: expense.amount,
      category: expense.category,
      fundId: expense.fundId,
      date: expense.date,
      description: expense.description,
      paymentMethod: expense.paymentMethod || '',
      checkNumber: expense.checkNumber || '',
      status: expense.status
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this draft permanently?")) return;
    await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'expenses', id));
    fetchExpenses();
  };

  const getFundName = (id) => funds.find(f => f.id === id)?.name || 'Unknown';

  const pendingExpenses = expenses.filter(e => e.status === 'pending_approval');
  const historyExpenses = expenses.filter(e => e.status !== 'pending_approval');
  const displayedExpenses = tabValue === 0 ? pendingExpenses : historyExpenses;

  const projectedBalance = selectedExpense ? currentFundBalance - parseFloat(selectedExpense.amount) : 0;
  const isNegative = projectedBalance < 0;

  const selectWrappingStyle = {
    sx: {
      '& .MuiSelect-select': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        height: 'auto'
      }
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 2
        }}
      >
        <Typography variant="h6">Expenditures & Payables</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1
          }}
        >
          <ExpenseBatchImport funds={funds} onSuccess={fetchExpenses} />
          <Button
            variant="contained"
            startIcon={<AddCircle />}
            onClick={handleNewExpense}
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            New Expense
          </Button>
        </Box>
      </Box>

      <Paper sx={{ overflow: 'hidden', width: '100%', minWidth: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minWidth: 'max-content' }}
          >
            <Tab
              label={<Badge badgeContent={pendingExpenses.length} color="error" sx={{ pr: 2 }}>Approval Queue</Badge>}
              icon={<AccessTime />}
              iconPosition="start"
            />
            <Tab label="History (Paid & Rejected)" icon={<History />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#fafafa' }}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Payee / Vendor</TableCell>
                <TableCell>Fund</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
  
            <TableBody>
              {displayedExpenses.map((row) => (
                <TableRow key={row.id} hover sx={{ opacity: row.status === 'rejected' ? 0.6 : 1 }}>
                  <TableCell>{row.date}</TableCell>
  
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {row.payee}
                    {row.status === 'rejected' && row.rejectionNote && (
                      <Typography variant="caption" display="block" color="error">
                        Reason: {row.rejectionNote}
                      </Typography>
                    )}
                    {row.checkNumber && (
                      <Chip label={`#${row.checkNumber}`} size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />
                    )}
                  </TableCell>
  
                  <TableCell>
                    <Chip label={getFundName(row.fundId)} size="small" variant="outlined" />
                  </TableCell>
  
                  <TableCell
                    align="right"
                    sx={{ textDecoration: row.status === 'rejected' ? 'line-through' : 'none' }}
                  >
                    ${parseFloat(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </TableCell>
  
                  <TableCell align="center">
                    {row.status === 'pending_approval' && (
                      <Chip label="Pending" color="warning" size="small" variant="outlined" />
                    )}
  
                    {row.status === 'approved' && (
                      row.reconciled ? (
                        <Tooltip title="Reconciled & Locked">
                          <Chip icon={<Lock />} label="Reconciled" color="default" size="small" variant="filled" />
                        </Tooltip>
                      ) : (
                        <Chip label="Approved" color="success" size="small" icon={<CheckCircle />} />
                      )
                    )}
  
                    {row.status === 'rejected' && <Chip label="Rejected" size="small" icon={<Block />} />}
                  </TableCell>
  
                  <TableCell align="right">
                    {row.status === 'pending_approval' && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
  
                        <Tooltip title={!canReview(row) ? "Self-Review Blocked" : "Review Request"}>
                          <span>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleOpenReview(row)}
                              disabled={!canReview(row)}
                            >
                              Review
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
  
                    {row.status === 'approved' && (isFinanceAdmin || isSuperAdmin) && (
                      <Tooltip title={row.reconciled ? "Cannot revert reconciled expense" : "Move back to Queue"}>
                        <span>
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => handleRevert(row)}
                            sx={{ fontSize: '0.7rem' }}
                            disabled={row.reconciled}
                          >
                            Revert
                          </Button>
                        </span>
                      </Tooltip>
                    )}
  
                    {row.status === 'rejected' && (isFinanceAdmin || isSuperAdmin) && (
                      <Tooltip title="Move back to Queue">
                        <IconButton size="small" onClick={() => handleRevert(row)}>
                          <Restore fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
  
              {displayedExpenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
          {displayedExpenses.map((row) => (
            <Paper
              key={row.id}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                opacity: row.status === 'rejected' ? 0.65 : 1,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 1
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
                    {row.payee}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.date}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    flexShrink: 0,
                    textDecoration: row.status === 'rejected' ? 'line-through' : 'none'
                  }}
                >
                  ${parseFloat(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                <Chip label={getFundName(row.fundId)} size="small" variant="outlined" />
                {row.checkNumber && <Chip label={`Check #${row.checkNumber}`} size="small" />}
                {row.paymentMethod && <Chip label={row.paymentMethod} size="small" variant="outlined" />}
                {row.status === 'pending_approval' && (
                  <Chip label="Pending" color="warning" size="small" variant="outlined" />
                )}
                {row.status === 'approved' && (
                  row.reconciled ? (
                    <Chip icon={<Lock />} label="Reconciled" size="small" />
                  ) : (
                    <Chip label="Approved" color="success" size="small" icon={<CheckCircle />} />
                  )
                )}
                {row.status === 'rejected' && <Chip label="Rejected" size="small" icon={<Block />} />}
              </Box>

              {row.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, overflowWrap: 'anywhere' }}>
                  {row.description}
                </Typography>
              )}

              {row.status === 'rejected' && row.rejectionNote && (
                <Alert severity="error" sx={{ mb: 1.25 }}>
                  {row.rejectionNote}
                </Alert>
              )}

              {row.status === 'pending_approval' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(row)}>
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenReview(row)}
                    disabled={!canReview(row)}
                  >
                    Review
                  </Button>
                </Box>
              )}

              {row.status === 'approved' && (isFinanceAdmin || isSuperAdmin) && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  onClick={() => handleRevert(row)}
                  disabled={row.reconciled}
                >
                  {row.reconciled ? 'Reconciled & Locked' : 'Move Back to Queue'}
                </Button>
              )}

              {row.status === 'rejected' && (isFinanceAdmin || isSuperAdmin) && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Restore />}
                  onClick={() => handleRevert(row)}
                >
                  Move Back to Queue
                </Button>
              )}
            </Paper>
          ))}

          {displayedExpenses.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No items found.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* --- REVIEW DIALOG --- */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: rejectMode ? '#ffebee' : 'inherit' }}>
          {rejectMode ? <Block color="error" /> : <AccountBalanceWallet color="primary" />}
          {rejectMode ? "Reject Expense Request" : "Review & Approve"}
        </DialogTitle>

        <DialogContent>
          {selectedExpense && (
            <Box sx={{ mt: 1 }}>
              {rejectMode ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You are rejecting this request. It will be moved to history. Funds are not affected.
                  </Alert>

                  <Typography variant="subtitle2" gutterBottom>
                    Reason for Rejection (Required):
                  </Typography>

                  <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g. Duplicate, Over budget, Wrong vendor..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    error={!rejectReason && rejectMode}
                  />
                </Box>
              ) : (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Payee</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedExpense.payee}</Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Fund</Typography>
                      <Typography variant="body1">{getFundName(selectedExpense.fundId)}</Typography>
                    </Grid>

                    {selectedExpense.paymentMethod && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Method</Typography>
                        <Typography variant="body2">{selectedExpense.paymentMethod}</Typography>
                      </Grid>
                    )}

                    {selectedExpense.checkNumber && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Check #</Typography>
                        <Typography variant="body2">{selectedExpense.checkNumber}</Typography>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Description</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                        <Typography variant="body2">{selectedExpense.description || "None"}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Fund Analysis
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: isNegative ? '#ffebee' : '#f1f8e9',
                      borderColor: isNegative ? 'error.main' : 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Current Balance:</Typography>
                      <Typography>${currentFundBalance.toLocaleString()}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'error.main' }}>
                      <Typography>Less Expense:</Typography>
                      <Typography>- ${parseFloat(selectedExpense.amount).toLocaleString()}</Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography fontWeight="bold">Projected:</Typography>
                      <Typography fontWeight="bold" color={isNegative ? 'error.main' : 'success.main'}>
                        ${projectedBalance.toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>

                  {isNegative && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <strong>Warning:</strong> Fund will be overdrawn.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {rejectMode ? (
            <>
              <Button onClick={() => setRejectMode(false)}>Back</Button>
              <Button variant="contained" color="error" onClick={confirmRejection}>Confirm Rejection</Button>
            </>
          ) : (
            <>
              <Button color="error" onClick={() => setRejectMode(true)}>Reject</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color={isNegative ? "error" : "success"}
                onClick={confirmApproval}
                startIcon={<CheckCircle />}
              >
                {isNegative ? "Approve Anyway" : "Approve"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* --- CREATE/EDIT DIALOG --- */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExpense ? "Edit Expense" : "New Expense"}</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box>
              <TextField
                select
                fullWidth
                label="Fund Source"
                value={formData.fundId}
                onChange={(e) => setFormData({ ...formData, fundId: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={selectWrappingStyle}
              >
                {funds.map(f => (
                  <MenuItem key={f.id} value={f.id} sx={{ whiteSpace: 'normal' }}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <Autocomplete
                freeSolo
                fullWidth
                options={vendors}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
                value={formData.payee}
                onInputChange={(e, val) => {
                  const safeVal = val || '';
                  const found = vendors.find(v => v.name && v.name.toLowerCase() === safeVal.toLowerCase());
                  setFormData(prev => ({
                    ...prev,
                    payee: safeVal,
                    category: found?.defaultCategory || prev.category
                  }));
                }}
                onChange={(e, val) => {
                  const name = typeof val === 'string' ? val : (val?.name || '');
                  const cat = typeof val === 'object' ? val?.defaultCategory : '';
                  setFormData(prev => ({
                    ...prev,
                    payee: name,
                    category: cat || prev.category
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Payee / Vendor"
                    placeholder="Who is being paid?"
                    multiline
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>

            <Box>
              <Autocomplete
                freeSolo
                fullWidth
                options={categories}
                value={formData.category}
                onChange={(e, val) => setFormData({ ...formData, category: val })}
                onInputChange={(e, val) => setFormData({ ...formData, category: val })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Category"
                    multiline
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>

            {/* Use the Material UI v7 Grid API so both fields receive their intended width. */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={DEFAULT_PAYMENT_METHODS}
                  value={formData.paymentMethod || ''}
                  onChange={(e, val) => setFormData({ ...formData, paymentMethod: val || '' })}
                  onInputChange={(e, val) => setFormData({ ...formData, paymentMethod: val || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Payment Method"
                      placeholder="Optional"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Check Number"
                  value={formData.checkNumber || ''}
                  onChange={(e) => setFormData({ ...formData, checkNumber: e.target.value })}
                  placeholder="e.g. 1045"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {editingExpense ? "Save Changes" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseManager;
